import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils.js";
import type { Scale } from "./SideBoard.js";
import { Vinyl } from "./Vinyl.js";

/**
 * How often the number is recomputed.
 *
 * Twice a second rather than every frame: the display runs in a television's
 * browser and `.claude/rules/frontend.md` rules out an animation-heavy idle
 * loop. Nothing here reads layout, so a tick is one text node and one width.
 */
const TICK_MS = 500;

/** Under this many seconds the countdown starts saying so. */
const URGENT_SECONDS = 5;

/**
 * How long this phase has left — the same clock on every screen in the room.
 *
 * # Why it takes a moment and not a duration
 *
 * The engine has armed timers since phase 4 and none of them reached the wire
 * until phase 14, so for nine phases a player could see that nothing was
 * happening and never that something was about to. The server publishes
 * *milliseconds from now* (`PhaseClock`), `gameLink` anchors that against this
 * device's own clock the moment it arrives, and this counts down to the result.
 * So a television whose clock is a minute out counts the same thirty seconds as
 * the phones — the difference between the two clocks never enters the sum.
 *
 * # It renders nothing when nothing is timing
 *
 * `TurnReady` waits for a person and a finished game waits for nobody. A
 * countdown there would be a clock running towards nothing, which is the exact
 * "why is it stuck?" a timer exists to answer.
 *
 * # The clock is the record — finding 3.15, settled in 20d
 *
 * *"The timer looks bad and is not noticeable by any player."* It was a 40px
 * hairline rule under two small digits, in the top-right of a header already
 * carrying a round label, an instruction and a coin count — on a 360px phone it
 * was the least visible thing on screen and it was the only thing counting.
 *
 * 20c made it a ring around a number. 20d makes it **the mark**: the same disc
 * as the logo and the same disc that spins while a clip plays, with the violet
 * ring running all the way round the outside and contracting back to twelve
 * o'clock as the phase empties, and the seconds on the label.
 *
 * 📌 **This is the maintainer's design, and it dissolved the objection to it.**
 * The earlier proposal was to put the ring around the *spinning* record on
 * stage, which would have been a second presentation of one clock — the record
 * is absent in `Placing`, `Challenge` and `Dispute`, which are also timed.
 * Making the clock itself a record is not that: there is exactly one countdown,
 * it is in one place in every phase, and it now reads as part of the game
 * instead of as chrome. ⚠️ **Never draw a second timer**, and never a bar.
 *
 * ⚠️ The disc it borrows is [`Vinyl`](./Vinyl.js), which does not spin while it
 * counts — a rotating ring says nothing about where in the phase we are.
 *
 * # Ten-foot rules
 *
 * The number is the message and the ring is the reinforcement — never colour
 * alone, and the seconds are in the accessible name rather than implied by an
 * icon. Motion is behind `motion-safe:`, so a viewer who asked for less gets a
 * countdown that steps rather than slides.
 *
 * ⚠️ **This renders on the television too** (`display/Board.tsx`, `scale="room"`),
 * which is a ten-foot UI with an underpowered browser. The ring animates one
 * `stroke-dashoffset` twice a second — no layout is read, nothing reflows.
 */
export function PhaseTimer({
  endsAt,
  seconds,
  scale = "near",
  spinning = false,
  size,
  className,
}: {
  /**
   * When the phase runs out, as a local `Date.now()` timestamp — `GameView`'s
   * `phaseEndsAt`. `null` when nothing is timing this phase.
   */
  endsAt: number | null;
  /**
   * How long the phase gets in total, for the ring's denominator. `null` draws
   * the label and the number with no ring, which is what an unmeasurable
   * countdown honestly is.
   */
  seconds: number | null;
  /** How far away the reader is — sets the default size. */
  scale?: Scale;
  /** Whether a clip is playing: the highlight turns and the ring moves inward. */
  spinning?: boolean;
  /** Edge length in pixels, overriding the one `scale` implies. */
  size?: number;
  className?: string;
}) {
  if (endsAt === null) {
    return null;
  }
  // **Keyed by the deadline**, so a new phase gets a component whose clock
  // starts now rather than one holding a `now` from whenever it last ticked.
  // Between phases there is no countdown and therefore no interval running, so
  // that held value can be a minute stale — and the first frame of the next
  // phase would flash a number nobody can explain. Remounting is React's own
  // answer to "reset this state when that input changes", and it keeps the
  // effect below a plain subscription rather than a cascading render.
  return (
    <Countdown
      key={endsAt}
      endsAt={endsAt}
      seconds={seconds}
      scale={scale}
      spinning={spinning}
      {...(size === undefined ? {} : { size })}
      {...(className === undefined ? {} : { className })}
    />
  );
}

function Countdown({
  endsAt,
  seconds,
  scale,
  spinning,
  size: given,
  className,
}: {
  endsAt: number;
  seconds: number | null;
  scale: Scale;
  spinning: boolean;
  size?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const ticking = window.setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      window.clearInterval(ticking);
    };
  }, []);

  const leftMs = Math.max(0, endsAt - now);
  // Rounded **up**, so a countdown reaches zero when the phase does rather than
  // sitting on "0" for a whole second while there is still time to answer.
  const left = Math.ceil(leftMs / 1000);
  const fraction = seconds && seconds > 0 ? Math.min(1, leftMs / (seconds * 1000)) : null;
  const urgent = left <= URGENT_SECONDS;
  const size = given ?? (scale === "room" ? 132 : 88);

  return (
    <div
      role="timer"
      data-testid="phase-timer"
      aria-label={t("timer.left", { count: left })}
      className={cn("relative shrink-0", className)}
      style={{ width: size, height: size }}
    >
      <Vinyl size={size} spinning={spinning} countdown={{ fraction, urgent }} />

      {/* The number is HTML over the SVG rather than `<text>` inside it: it is
          `tabular-nums` so two digits do not shuffle the centre as they tick,
          and the spoken form below is a translated string, which belongs in the
          document rather than in the artwork. */}
      <p
        className={cn(
          "absolute inset-0 flex items-center justify-center font-bold leading-none tabular-nums",
          // Dark on the lit label, in both states — the label is
          // `flash-500`, or `wrong-500` when time is nearly out, and both are
          // fills this reads against at better than 4.5:1. Urgency is the ring
          // emptying and the label changing, never the digits alone.
          "text-stage-950",
        )}
        // Derived from the disc rather than picked from the type scale: the
        // label is a fixed fraction of the record, so a step class would fit at
        // one size and overflow or rattle at the next.
        style={{ fontSize: size * 0.34 }}
      >
        {left}
        {/* The unit is spoken, not drawn: at ten feet the digits are the
            message, and "8 seconds left" read aloud beats "8". */}
        <span className="sr-only"> {t("timer.left", { count: left })}</span>
      </p>
    </div>
  );
}
